import React, { createContext, useContext, useState, useEffect } from 'react';
import CryptoJS from 'crypto-js';

// Stored password hashes
const VALID_HASHES = {
  upper: process.env.REACT_APP_PASSWORD_HASH_UPPER || '',
  lower: process.env.REACT_APP_PASSWORD_HASH_LOWER || ''
};

// Hash function for password verification
const hashPassword = (password: string): string => {
  return CryptoJS.SHA256(password.toLowerCase()).toString();
};

// Use a random string as a token
const generateToken = () => Math.random().toString(36).substring(2);

interface AuthContextType {
  isAuthenticated: boolean;
  login: (password: string) => boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_KEY = 'nb_auth_token';

const checkAuthStatus = () => {
  const token = localStorage.getItem(AUTH_KEY);
  const lastLogin = localStorage.getItem('nb_last_login');
  
  if (token && lastLogin) {
    const loginDate = new Date(lastLogin);
    const now = new Date();
    const daysDiff = (now.getTime() - loginDate.getTime()) / (1000 * 3600 * 24);
    
    if (daysDiff <= 30) {
      return true;
    }
    
    // Clear expired session
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem('nb_last_login');
  }
  
  return false;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Always authenticated
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(true);


  const login = (password: string): boolean => {
    // Convert input password to lowercase
    const lowerPassword = password.toLowerCase();
    const hashedPassword = hashPassword(lowerPassword);
    
    // Convert stored hashes to lowercase versions for comparison
    const lowerHash = hashPassword('nb=unicorn');
    
    if (hashedPassword === lowerHash) {
      const token = generateToken();
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_KEY, token);
      localStorage.setItem('nb_last_login', new Date().toISOString());
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem('nb_last_login');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
